import { Link } from "react-router-dom";
import { Trophy, ArrowRight, TrendUp, Target, ChartBar, ArrowsClockwise, Timer } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";

const CATEGORIES = [
  {
    id: "flagship", label: "FLAGSHIP REPORTS", icon: Trophy, color: "#ccff00",
    articles: [
      { href: "/research/championship-report", title: "What It Takes to Win", desc: "The full championship analysis: 6,976 rosters, every finding, methodology.", badge: "FLAGSHIP" },
      { href: "/research/what-championship-rosters-have-in-common", title: "What Championship Rosters Have in Common", desc: "9,983 rosters. Elite RBs, concentrated value, 27.5 avg age." },
      { href: "/championship-blueprint", title: "Championship Blueprint", desc: "Quick-view data dashboard with key stats and player lists." },
    ],
  },
  {
    id: "trading", label: "TRADING", icon: TrendUp, color: "#45b7d1",
    articles: [
      { href: "/research/do-teams-that-trade-more-win", title: "Do Teams That Trade More Win?", desc: "167,265 real trades. Not how much — it's when." },
      { href: "/research/what-winning-trades-look-like", title: "What a Winning Trade Looks Like", desc: "Timing, direction, and the courage to overpay." },
      { href: "/research/who-wins-lopsided-trades", title: "Who Wins Lopsided Trades?", desc: "The \"loser\" wins titles more often." },
      { href: "/research/when-to-make-your-move", title: "When to Make Your Move", desc: "Champions traded 68% more in playoff weeks." },
      { href: "/research/how-lopsided-is-the-average-trade", title: "The Average Trade Is 40% Lopsided", desc: "Full value-gap distribution." },
    ],
  },
  {
    id: "rosters", label: "CHAMPIONSHIP ROSTERS", icon: Target, color: "#4ecdc4",
    articles: [
      { href: "/research/stars-vs-depth", title: "Stars vs Depth", desc: "38.3% of value in 3 players." },
      { href: "/research/where-champions-put-their-value", title: "Where Champions Put Their Value", desc: "WR 35.7%, RB 30.1%, QB 22.5%, TE 11.7%." },
      { href: "/research/how-many-studs-to-win", title: "How Many Studs to Win?", desc: "Average: 1.07 elite players." },
      { href: "/research/championship-dna-players", title: "Championship DNA Players", desc: "Bijan 23%, Puka 19.3%." },
      { href: "/research/do-you-need-an-elite-qb", title: "Do You Need an Elite QB?", desc: "71.6% won without a top-3 QB." },
      { href: "/research/do-champions-pay-up-at-tight-end", title: "Do Winners Pay Up at TE?", desc: "11.7% of value, but 67% had a top-10 TE." },
      { href: "/research/the-real-age-of-a-champion", title: "The Real Age of a Champion", desc: "27.55 avg. Only 7.3% were young." },
      { href: "/research/how-champions-build-rosters", title: "How Champions Build Rosters", desc: "63% drafted or held. Trades: 21.5%." },
    ],
  },
  {
    id: "drafts", label: "DRAFTS", icon: ChartBar, color: "#ff6b6b",
    articles: [
      { href: "/research/startup-strategies-that-win", title: "Startup Strategies That Win", desc: "RB-Heavy 12.36%. QB-Early 6.14%." },
      { href: "/research/what-happens-in-startup-drafts", title: "What Happens in Startup Drafts", desc: "46% of round 1 is QBs (SF)." },
      { href: "/research/which-rookie-picks-hit", title: "Which Rookie Picks Hit?", desc: "1.01 hits 91.9%. Mid-second: single digits." },
      { href: "/research/draft-picks-that-win-championships", title: "Draft Picks That Win Championships", desc: "Rookie R1: 25.5% of self-drafted core." },
    ],
  },
  {
    id: "rebuilds", label: "REBUILDS & CYCLES", icon: ArrowsClockwise, color: "#f9ca24",
    articles: [
      { href: "/research/how-long-a-rebuild-takes", title: "How Long a Rebuild Takes", desc: "Avg 1.64 seasons. 86% within two years." },
      { href: "/research/fastest-way-out-of-a-rebuild", title: "Fastest Way Out of a Rebuild", desc: "All approaches: ~1.3 seasons." },
      { href: "/research/do-dynasties-repeat", title: "Do Dynasties Repeat?", desc: "15.6% multi-title. 11.6% back-to-back." },
    ],
  },
  {
    id: "aging", label: "AGING & VALUE", icon: Timer, color: "#c084fc",
    articles: [
      { href: "/research/when-players-lose-value-by-age", title: "When Each Position Falls Off", desc: "RBs cliff 26-27. QBs hold to 30s." },
      { href: "/research/how-long-dynasty-assets-hold-value", title: "How Long Assets Hold Value", desc: "QBs: ~10yr. WRs: 5-7. RBs: 2-3." },
      { href: "/research/when-running-backs-fall-off", title: "When Running Backs Fall Off", desc: "~47% at 27, ~31% at 28, ~11% at 30." },
      { href: "/research/why-elite-wrs-are-safest", title: "Why Elite WRs Are Safest", desc: "Gentle decline vs RB cliff." },
      { href: "/research/superflex-qb-longevity", title: "Superflex QB Longevity", desc: "~61% of peak at 30." },
    ],
  },
];

function getSlug(href) {
  return href.split("/").pop();
}

// Hero images available (generated, not text-based OG cards)
const HERO_IMAGES = new Set([
  "do-teams-that-trade-more-win", "what-winning-trades-look-like",
  "who-wins-lopsided-trades", "when-to-make-your-move",
  "how-lopsided-is-the-average-trade",
  "stars-vs-depth", "where-champions-put-their-value",
  "how-many-studs-to-win", "championship-dna-players",
  "do-you-need-an-elite-qb", "do-champions-pay-up-at-tight-end",
  "the-real-age-of-a-champion", "how-champions-build-rosters",
  "startup-strategies-that-win", "what-happens-in-startup-drafts",
  "which-rookie-picks-hit", "draft-picks-that-win-championships",
  "how-long-a-rebuild-takes", "fastest-way-out-of-a-rebuild",
  "do-dynasties-repeat", "when-players-lose-value-by-age",
  "how-long-dynasty-assets-hold-value", "when-running-backs-fall-off",
  "why-elite-wrs-are-safest", "superflex-qb-longevity",
]);

function ArticleCard({ article, color }) {
  const slug = getSlug(article.href);
  const hasHero = HERO_IMAGES.has(slug);
  const hasOG = !article.href.includes("championship-blueprint") && !article.href.includes("how-lopsided") && !hasHero;
  const imgSrc = hasHero ? `/hero-images/${slug}.jpg` : `/share-cards/${slug}.png`;
  const showImage = hasHero || hasOG;
  return (
    <Link to={article.href} className="group border border-[#262626] bg-[#171717] rounded-sm overflow-hidden no-underline hover:border-[#333] transition-colors" data-testid={`research-card-${slug}`}>
      {showImage && (
        <div className="aspect-[1200/630] overflow-hidden bg-[#0d0d0d]">
          <img src={imgSrc} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <h3 className="text-sm text-white font-semibold group-hover:text-[#ccff00] transition-colors">{article.title}</h3>
          {article.badge && <span className="text-[7px] px-1.5 py-0.5 border rounded-sm font-bold shrink-0" style={{ color, borderColor: `${color}50`, backgroundColor: `${color}10` }}>{article.badge}</span>}
        </div>
        <p className="text-[11px] text-[#666] leading-relaxed">{article.desc}</p>
      </div>
    </Link>
  );
}

export default function ResearchIndex() {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Dynasty Championship Research — 33 Data-Backed Articles — Dynasty Trade Maker" description="33 original research articles analyzing dynasty fantasy football: trades, rosters, drafts, rebuilds, and aging curves. Every finding from real Sleeper league data." path="/research" />
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <Trophy size={28} className="text-[#ccff00] mx-auto mb-3" weight="fill" />
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-3">CHAMPIONSHIP <span className="text-[#ccff00]">RESEARCH</span></h1>
          <p className="text-base text-[#a3a3a3] max-w-2xl mx-auto mb-2">{"33 original data analyses from 6,976 dynasty league champions."}</p>
          <p className="text-xs text-[#555]">{"167,265 trades \u00b7 9,983 champion rosters \u00b7 7,155 drafts \u00b7 172,080 historical rosters"}</p>
        </div>

        {/* Category Jump Links */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {CATEGORIES.map(cat => (
            <a key={cat.id} href={`#${cat.id}`} className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 border rounded-sm no-underline transition-colors hover:bg-white/5" style={{ color: cat.color, borderColor: `${cat.color}30` }}>
              {cat.label}
            </a>
          ))}
        </div>

        {/* Categories with Card Grids */}
        <div className="space-y-12 mb-10">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            return (
              <section key={cat.id} id={cat.id}>
                <div className="flex items-center gap-2 mb-5">
                  <Icon size={18} weight="bold" style={{ color: cat.color }} />
                  <h2 className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: cat.color }}>{cat.label}</h2>
                  <div className="flex-1 h-px ml-2" style={{ backgroundColor: `${cat.color}20` }} />
                  <span className="text-[10px] text-[#555]">{cat.articles.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cat.articles.map(article => (
                    <ArticleCard key={article.href} article={article} color={cat.color} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* Legacy spokes */}
        <div className="border-t border-[#262626] pt-8 mb-8">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#888] mb-5">EARLIER CHAMPIONSHIP SPOKES</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { href: "/research/qb-question", title: "Do You Need a Top-5 QB?", desc: "Championship rates by QB tier, split SF vs 1QB.", icon: ChartBar },
              { href: "/research/league-winners", title: "The 2025 League Winners", desc: "Top 25 players by championship rate, indexed against rostership.", icon: Trophy },
              { href: "/research/stars-and-scrubs", title: "Stars and Scrubs vs Depth", desc: "Value concentration in top-3 players. 36.5% of total value.", icon: Target },
              { href: "/research/age-of-champion", title: "The Age of a Champion", desc: "Avg roster age 27.4. Position-level breakdown.", icon: Timer },
              { href: "/research/elite-te-question", title: "Do Champions Pay Up at TE?", desc: "44.3% had a top-5 TE. The elite TE advantage by format.", icon: Target },
              { href: "/research/positional-blueprint", title: "Positional Blueprint", desc: "WR 35%, RB 30%, QB 23%, TE 12%. SF vs 1QB allocation table.", icon: ChartBar },
            ].map(page => {
              const Icon = page.icon;
              return (
                <Link key={page.href} to={page.href} className="group p-4 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline transition-colors rounded-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-sm bg-[#ccff00]/10 flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-[#ccff00]" weight="bold" />
                    </div>
                    <div>
                      <p className="text-sm text-white font-semibold mb-1 group-hover:text-[#ccff00] transition-colors">{page.title}</p>
                      <p className="text-[11px] text-[#666] leading-relaxed">{page.desc}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="text-center text-[10px] text-[#444] space-x-3">
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guides</Link>
        </div>
      </div>
    </div>
  );
}
